import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LinkContext, WorkItem } from '@captain-tasko/shared';
import { apiFetch, apiList, apiSingle } from '../lib/api.js';
import { formatWorkItemKey } from '../lib/format.js';
import { toast } from './Toast.js';

const CONTEXTS: { value: LinkContext; label: string }[] = [
  { value: 'source', label: 'Source' },
  { value: 'reference', label: 'Reference' },
  { value: 'requirement', label: 'Requirement' },
  { value: 'evidence', label: 'Evidence' },
  { value: 'output', label: 'Output' },
  { value: 'attachment', label: 'Attachment' },
];

interface LinkedWorkItem {
  workItemId: string;
  workItemKey: string;
  /** Importer-set source identifier. When present, surface this as
   *  the primary display label and demote workItemKey to chrome.
   *  See FEATURE_REQUESTS.md "Align display key with external_id." */
  workItemExternalId?: string | null;
  title: string;
  status: string;
  priority: string;
  context: LinkContext;
  note: string | null;
  createdBy: string;
  createdAt: string;
}

export function LinkedWorkItemsPanel({
  documentId,
  onNavigate,
}: {
  documentId: string;
  onNavigate: (p: string) => void;
}) {
  const qc = useQueryClient();
  const links = useQuery({
    queryKey: ['document-work-links', documentId],
    queryFn: () => apiList<LinkedWorkItem>(`/documents/${documentId}/links/work-items`),
  });

  const allItems = useQuery({
    queryKey: ['workitems', { all: true }],
    queryFn: () => apiList<WorkItem>('/workitems', { query: { limit: 200 } }),
  });

  const [picking, setPicking] = useState(false);
  const [pickWorkItem, setPickWorkItem] = useState('');
  const [pickContext, setPickContext] = useState<LinkContext>('reference');
  const [pickNote, setPickNote] = useState('');

  const addLink = useMutation({
    mutationFn: () =>
      apiSingle(`/documents/${documentId}/links/work-items`, {
        method: 'POST',
        body: { workItemId: pickWorkItem, context: pickContext, note: pickNote || undefined },
      }),
    onSuccess: () => {
      toast.success('Linked');
      setPicking(false);
      setPickWorkItem('');
      setPickNote('');
      qc.invalidateQueries({ queryKey: ['document-work-links', documentId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const removeLink = useMutation({
    mutationFn: (workItemId: string) =>
      apiFetch<void>(`/documents/${documentId}/links/work-items/${workItemId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['document-work-links', documentId] }),
  });

  const existingIds = new Set(links.data?.data.map((l) => l.workItemId) ?? []);
  const linkableItems = (allItems.data?.data ?? []).filter((it) => !existingIds.has(it.id));

  return (
    <section style={{ background: 'var(--surface-raised)', padding: 16, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
          Linked work items
        </h3>
        <button className="btn btn--ghost" onClick={() => setPicking((v) => !v)}>
          {picking ? 'Cancel' : '+ Link'}
        </button>
      </div>

      {picking && (
        <div style={{ background: 'var(--surface-sunken)', padding: 8, borderRadius: 4, marginBottom: 8 }}>
          <div className="field" style={{ marginBottom: 6 }}>
            <label htmlFor="lw-item">Work item</label>
            <select
              id="lw-item"
              value={pickWorkItem}
              onChange={(e) => setPickWorkItem(e.target.value)}
              style={{ width: '100%', padding: 4 }}
            >
              <option value="">Choose a work item…</option>
              {linkableItems.map((it) => {
                // CTSK-174: key is primary; externalId shown only when
                // materially different from lowercase key (matches the
                // convention `formatWorkItemKey` implements everywhere
                // else in the app). Prior behavior led with externalId,
                // which shadowed the canonical Tasko key on operator-
                // created workitems that carry cross-refs as externalId.
                const display = formatWorkItemKey(it);
                return (
                  <option key={it.id} value={it.id}>{display.combined} — {it.title}</option>
                );
              })}
            </select>
          </div>
          <div className="field" style={{ marginBottom: 6 }}>
            <label htmlFor="lw-ctx">Context</label>
            <select
              id="lw-ctx"
              value={pickContext}
              onChange={(e) => setPickContext(e.target.value as LinkContext)}
              style={{ width: '100%', padding: 4 }}
            >
              {CONTEXTS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div className="field" style={{ marginBottom: 6 }}>
            <label htmlFor="lw-note">Note (optional)</label>
            <input id="lw-note" value={pickNote} onChange={(e) => setPickNote(e.target.value)} style={{ width: '100%' }} />
          </div>
          <button
            className="btn btn--primary"
            disabled={!pickWorkItem || addLink.isPending}
            onClick={() => addLink.mutate()}
            style={{ width: '100%' }}
          >
            {addLink.isPending ? 'Linking…' : 'Link'}
          </button>
        </div>
      )}

      {links.isLoading && <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>Loading…</div>}
      {links.data && links.data.data.length === 0 && (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No work items linked yet.</div>
      )}
      {links.data && links.data.data.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {links.data.data.map((l) => (
            <div key={l.workItemId} style={{ padding: 8, border: '1px solid var(--border-subtle)', borderRadius: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  <code
                    style={{ fontFamily: 'var(--font-mono)' }}
                    title={l.workItemExternalId ? l.workItemKey : undefined}
                  >
                    {l.workItemExternalId ?? l.workItemKey}
                  </code> · {l.context}
                </span>
                <button
                  onClick={() => removeLink.mutate(l.workItemId)}
                  aria-label="Remove link"
                  style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 14 }}
                >
                  ×
                </button>
              </div>
              <a
                href={`/workitems/${l.workItemId}`}
                onClick={(e) => { e.preventDefault(); onNavigate(`/workitems/${l.workItemId}`); }}
                style={{ display: 'block', marginTop: 2 }}
              >
                {l.title}
              </a>
              {l.note && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>“{l.note}”</div>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
